import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import ReactECharts from "echarts-for-react";

type ScreenId = 1 | 2 | 3 | 4 | 5;

type AuthPayload = {
  access_token?: string;
  iin?: string;
};

type BootstrapResponse = {
  available_mis_ids?: string[];
  default_days?: number;
  last_refresh?: {
    hospital?: string;
    queue?: string;
  };
};


type OrgOption = {
  id: string;
  label: string;
};

type Point = { date: string; value: number };

type ScreenSeries = {
  metric_code: string;
  metric_name: string;
  points?: Point[];
};

type ScreenResponse = {
  series?: ScreenSeries[];
  last_refresh_at?: string;
};

type Screen5Point = {
  date: string;
  value: number;
  target_count: number;
  actual_count: number;
};

type Screen5Series = {
  series_code: string;
  series_name: string;
  series_name_ru: string;
  points?: Screen5Point[];
};

type Screen5Response = {
  screen_no?: number;
  date_from?: string;
  date_to?: string;
  scope?: { mode?: string; mis_id?: string };
  series?: Screen5Series[];
  last_refresh_at?: string;
};

type QueueBar = {
  queue_category_code: string;
  queue_category_name: string;
  total_value: number;
  remaining_value: number;
};

type QueueResponse = {
  bars?: QueueBar[];
  last_refresh_at?: string;
};

type TreatmentReasonGroupItem = {
  purpose_code: number;
  purpose_name: string;
  total_count: number;
};

type TreatmentReasonGroup = {
  group_code: number;
  group_name: string;
  items?: TreatmentReasonGroupItem[];
};

type TreatmentReasonResponse = {
  groups?: TreatmentReasonGroup[];
  last_refresh_at?: string;
};

type MetricView = {
  id: string;
  title: string;
  color: string;
  buckets: string[];
  values: number[];
  currentValue: number;
  periodLabel: string;
};

const PAGE_BG = "#083264";
const PANEL_BG = "#0e2b4b";
const ACTION_BG = "#083264";
const BORDER = "1px solid rgba(255,255,255,0.2)";
const CONTROL_BORDER = "1px solid rgba(255,255,255,0.3)";
const TEXT_MAIN = "#fff";
const TEXT_MUTED = "rgba(255,255,255,0.72)";
const COLORS = ["#48B1CC", "#eee36d", "#00B8BA", "#7E57C2"];
const POLL_MS = 3 * 60 * 1000;
const METRIC_CARD_COUNT = 4;

function isQueueDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("queue_demo") === "1" || params.get("queue_demo") === "true";
}

function makeDemoQueueBars(bars: QueueBar[]): QueueBar[] {
  return bars.map((bar) => {
    const total_value = Math.floor(Math.random() * 51);
    const remaining_value = total_value === 0 ? 0 : Math.floor(Math.random() * (total_value + 1));
    return {
      ...bar,
      total_value,
      remaining_value,
    };
  });
}

function queueScaleValue(value: number): number {
  return value > 0 ? Math.log1p(value) : 0;
}

function queueUnscaleValue(value: number): number {
  return value > 0 ? Math.round(Math.expm1(value)) : 0;
}

function getAuthPayload(): AuthPayload | null {
  try {
    const raw = localStorage.getItem("auth_payload");
    if (!raw) return null;
    return JSON.parse(raw) as AuthPayload;
  } catch {
    return null;
  }
}

async function fetchUserOrgsFromDefektolog(): Promise<OrgOption[]> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const res = await fetch("/api/v1/analytics/dicts", { headers, credentials: "omit" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }

  const data = (await res.json()) as { orgs?: Array<Record<string, unknown>> };
  const orgs = Array.isArray(data?.orgs) ? data.orgs : [];
  return orgs
    .map((org) => {
      const id = String(org?.orgId || "").trim();
      if (!id) return null;
      const label = String(org?.name || org?.orgName || org?.shortName || id).trim();
      return { id, label: label || id } satisfies OrgOption;
    })
    .filter((org): org is OrgOption => Boolean(org?.id))
    .sort((a, b) => a.label.localeCompare(b.label, "ru"));
}

async function apiFetch<T>(path: string, params: Record<string, string | number | undefined>): Promise<T> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;
  const role = (localStorage.getItem("dashboards_role") || "").trim();
  if (role) headers["X-Role"] = role;

  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") query.set(k, String(v));
  });

  const url = `/dashboards-api${path}${query.toString() ? `?${query.toString()}` : ""}`;
  const res = await fetch(url, { headers, credentials: "omit" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

function formatIso(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", { hour12: false });
}

function formatPointLabel(value: string): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const d = new Date(`${value}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
    }
  }
  return value;
}

function getPeriodLabel(points: Point[]): string {
  if (!points.length) return "Нет данных за выбранный период";
  const first = formatPointLabel(points[0]?.date || "");
  const last = formatPointLabel(points[points.length - 1]?.date || "");
  return first === last ? first : `${first} — ${last}`;
}

function lineOption(metric: MetricView) {
  return {
    color: [metric.color],
    tooltip: {
      trigger: "axis",
      backgroundColor: "#083264",
      borderColor: "rgba(255,255,255,0.25)",
      textStyle: { color: "#fff" },
      valueFormatter: (value: number) => `${value}`,
    },
    grid: { left: 44, right: 18, top: 24, bottom: 34 },
    xAxis: {
      type: "category",
      data: metric.buckets,
      boundaryGap: false,
      axisLine: { lineStyle: { color: "rgba(255,255,255,0.4)" } },
      axisLabel: { color: TEXT_MUTED, fontSize: 11 },
    },
    yAxis: {
      type: "value",
      axisLabel: { color: TEXT_MUTED, fontSize: 11 },
      splitLine: { lineStyle: { color: "rgba(255,255,255,0.12)" } },
    },
    series: [
      {
        type: "line",
        data: metric.values,
        smooth: true,
        symbol: "none",
        lineStyle: { width: 3, color: metric.color },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: `${metric.color}99` },
              { offset: 1, color: `${metric.color}0D` },
            ],
          },
        },
      },
    ],
  };
}

function buildDateRange(dateFrom: string, dateTo: string): string[] {
  if (!dateFrom || !dateTo) return [];
  const [startY, startM, startD] = dateFrom.split("-").map((value) => Number(value));
  const [endY, endM, endD] = dateTo.split("-").map((value) => Number(value));
  const start = new Date(Date.UTC(startY, startM - 1, startD));
  const end = new Date(Date.UTC(endY, endM - 1, endD));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const days: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function screen5Option(data: Screen5Response) {
  const series = Array.isArray(data.series) ? data.series : [];
  const categories = buildDateRange(data.date_from || "", data.date_to || "");
  const palette = ["#48B1CC", "#EEE36D", "#00B8BA", "#7E57C2", "#F6A04D", "#E04E5B", "#84D06F"];

  return {
    color: palette,
    tooltip: {
      trigger: "axis",
      backgroundColor: "#083264",
      borderColor: "rgba(255,255,255,0.25)",
      textStyle: { color: "#fff" },
      formatter: (params: Array<{ axisValue?: string; seriesName?: string; value?: number }>) => {
        if (!params.length) return "";
        const dateLabel = params[0]?.axisValue || "";
        const rows = params
          .map((item) => {
            const value = Number(item.value ?? 0);
            return `
              <div style="display:flex;justify-content:space-between;gap:12px;min-width:240px;">
                <span>${item.seriesName || ""}</span>
                <b>${value.toFixed(1)}%</b>
              </div>
            `;
          })
          .join("");
        return `<div style="min-width:260px;"><div style="font-weight:700;margin-bottom:6px;">${formatPointLabel(dateLabel)}</div>${rows}</div>`;
      },
    },
    legend: {
      top: 6,
      textStyle: { color: TEXT_MUTED, fontSize: 11 },
      data: series.map((item) => item.series_name_ru || item.series_name),
    },
    grid: { left: 56, right: 24, top: 54, bottom: 34 },
    xAxis: {
      type: "category",
      data: categories,
      boundaryGap: false,
      axisLine: { lineStyle: { color: "rgba(255,255,255,0.4)" } },
      axisLabel: { color: TEXT_MUTED, fontSize: 10, formatter: (value: string) => formatPointLabel(value) },
    },
    yAxis: {
      type: "value",
      axisLabel: { color: TEXT_MUTED, fontSize: 11, formatter: (value: number) => `${Number(value).toFixed(0)}%` },
      splitLine: { lineStyle: { color: "rgba(255,255,255,0.12)" } },
    },
    series: series.map((item, index) => {
      const points = Array.isArray(item.points) ? item.points : [];
      const valueByDate = new Map(points.map((point) => [point.date, Number(point.value || 0)]));
      return {
        name: item.series_name_ru || item.series_name,
        type: "line",
        data: categories.map((day) => valueByDate.get(day) ?? 0),
        smooth: true,
        symbol: "none",
        lineStyle: { width: 3, color: palette[index % palette.length] },
      };
    }),
  };
}

function queueOption(labels: string[], totalValues: number[], remainingValues: number[]) {
  const scaledTotalValues = totalValues.map((value) => queueScaleValue(value));
  const scaledRemainingValues = remainingValues.map((value) => queueScaleValue(value));
  const maxScaleValue = Math.max(
    1,
    ...scaledTotalValues,
    ...scaledRemainingValues,
  );

  return {
    color: ["#2F7DF6", "#123B63"],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#083264",
      borderColor: "rgba(255,255,255,0.25)",
      textStyle: { color: "#fff" },
      formatter: (params: Array<{ name?: string; seriesName?: string; value?: number; data?: { originalValue?: number } }>) => {
        if (!params.length) return "";
        const title = params[0]?.name || "";
        const rows = params
          .map((item) => {
            const originalValue = item.data?.originalValue ?? queueUnscaleValue(Number(item.value ?? 0));
            return `
              <div style="display:flex;justify-content:space-between;gap:12px;min-width:180px;">
                <span>${item.seriesName || ""}</span>
                <b>${originalValue}</b>
              </div>
            `;
          })
          .join("");
        return `<div style="min-width:220px;"><div style="font-weight:700;margin-bottom:6px;">${title}</div>${rows}</div>`;
      },
    },
    grid: { left: 240, right: 30, top: 16, bottom: 22 },
    xAxis: {
      type: "value",
      min: 0,
      max: maxScaleValue,
      axisLabel: {
        color: TEXT_MUTED,
        fontSize: 11,
        formatter: (value: number) => `${queueUnscaleValue(Number(value))}`,
      },
      splitLine: { lineStyle: { color: "rgba(255,255,255,0.12)" } },
    },
    yAxis: {
      type: "category",
      data: labels,
      axisLabel: { color: TEXT_MUTED, fontSize: 11 },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: "rgba(255,255,255,0.3)" } },
    },
    series: [
      {
        type: "bar",
        data: scaledTotalValues.map((value, index) => ({
          value,
          originalValue: totalValues[index] ?? 0,
        })),
        barWidth: 14,
        itemStyle: { color: "#2F7DF6", borderRadius: [0, 6, 6, 0] },
        label: {
          show: true,
          position: "insideRight",
          offset: [-6, 0],
          align: "right",
          verticalAlign: "middle",
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
          formatter: (params: { value: number; data?: { originalValue?: number } }) => {
            const originalValue = params.data?.originalValue ?? queueUnscaleValue(Number(params.value ?? 0));
            return originalValue === 0 ? "" : `${originalValue}`;
          },
        },
        z: 1,
      },
      {
        type: "bar",
        data: scaledRemainingValues.map((value, index) => ({
          value,
          originalValue: remainingValues[index] ?? 0,
        })),
        barWidth: 14,
        barGap: "-100%",
        itemStyle: { color: "#123B63", borderRadius: [0, 6, 6, 0] },
        label: {
          show: true,
          position: "insideLeft",
          align: "left",
          verticalAlign: "middle",
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
          formatter: (params: { value: number; data?: { originalValue?: number } }) => {
            const originalValue = params.data?.originalValue ?? queueUnscaleValue(Number(params.value ?? 0));
            return originalValue === 0 ? "" : `${originalValue}`;
          },
        },
        z: 2,
      },
    ],
  };
}

type TreatmentReasonFlatRow = {
  groupCode: number;
  groupName: string;
  purposeName: string;
  value: number;
  displayLabel: string;
};

function buildTreatmentReasonRows(groups: TreatmentReasonGroup[]): TreatmentReasonFlatRow[] {
  const rows: TreatmentReasonFlatRow[] = [];

  groups.forEach((group) => {
    const items = Array.isArray(group.items) ? group.items : [];
    items.forEach((item) => {
      rows.push({
        groupCode: group.group_code,
        groupName: group.group_name,
        purposeName: item.purpose_name,
        value: item.total_count,
        displayLabel: `${truncateWords(item.purpose_name, 4)}`,
      });
    });
  });

  return rows;
}

function truncateWords(value: string, limit = 4): string {
  const words = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words.slice(0, limit).join(" ");
}

  function treatmentReasonOption(rows: TreatmentReasonFlatRow[], isMobile: boolean) {
  const scaledValues = rows.map((row) => queueScaleValue(row.value));
  const maxValue = Math.max(1, ...scaledValues);
  const gridLeft = isMobile ? 150 : 220;
  const labelWidth = isMobile ? 120 : 220;
  const labelFontSize = isMobile ? 8 : 9;
  const chartHeight = Math.max(isMobile ? 960 : 1040, rows.length * (isMobile ? 24 : 26));

  return {
    chartHeight,
    option: {
      color: ["#2F7DF6"],
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        confine: true,
        appendToBody: false,
        backgroundColor: "#083264",
        borderColor: "rgba(255,255,255,0.25)",
        textStyle: { color: "#fff" },
        formatter: (params: Array<{ name?: string; value?: number; dataIndex?: number }>) => {
          if (!params.length) return "";
          const first = params[0];
          const row = rows[first?.dataIndex ?? 0];
          if (!row) return "";
          return `
            <div style="min-width:220px;">
              <div style="font-weight:700;margin-bottom:6px;">${row.groupName}</div>
              <div style="display:flex;justify-content:space-between;gap:12px;">
                <span>${row.purposeName}</span>
                <b>${Number(row.value)}</b>
              </div>
            </div>
          `;
        },
      },
      grid: { left: gridLeft, right: 24, top: 16, bottom: 20 },
      xAxis: {
        type: "value",
        min: 0,
        max: maxValue,
        axisLabel: {
          color: TEXT_MUTED,
          fontSize: 11,
          formatter: (value: number) => `${queueUnscaleValue(Number(value))}`,
        },
        splitLine: { lineStyle: { color: "rgba(255,255,255,0.12)" } },
      },
      yAxis: {
        type: "category",
        data: rows.map((row) => row.displayLabel),
        axisLabel: {
          color: TEXT_MUTED,
          fontSize: labelFontSize,
          width: labelWidth,
          overflow: "truncate",
          align: "right",
          lineHeight: 12,
          margin: 10,
          hideOverlap: false,
        },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "rgba(255,255,255,0.3)" } },
      },
      series: [
        {
          type: "bar",
          data: rows.map((row, index) => ({
            value: scaledValues[index] ?? 0,
            originalValue: row.value,
          })),
          barWidth: isMobile ? 10 : 12,
          itemStyle: { color: "#2F7DF6", borderRadius: [0, 6, 6, 0] },
          label: {
            show: true,
            position: "insideRight",
            offset: [2, 0],
            align: "right",
            verticalAlign: "middle",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            formatter: (params: { value: number; data?: { originalValue?: number } }) =>
              Number(params.data?.originalValue ?? params.value ?? 0) === 0
                ? ""
                : `${params.data?.originalValue ?? queueUnscaleValue(Number(params.value ?? 0))}`,
          },
        },
      ],
    },
  };
}

const pageCss: CSSProperties = {
  height: "100vh",
  minHeight: "100vh",
  background: PAGE_BG,
  color: TEXT_MAIN,
  margin: 0,
  fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
};

const containerCss: CSSProperties = {
  width: "100%",
  maxWidth: 1400,
  margin: "0 auto",
  padding: "10px",
  boxSizing: "border-box",
};

const menuRoutes = [
  { label: "Главная", href: "/" },
  { label: "Dashboards АПП", href: "/dashboards/app" },
  { label: "Dashboards КС", href: "/dashboards/hospital?place=%D0%9A%D0%A1" },
  { label: "Охрана материнства", href: "/dashboards/mother-hospital" },
  { label: "Dashboards ДС", href: "/dashboards/hospital?place=%D0%94%D0%A1" },
  { label: "Ситуационный центр", href: "/dashboards/situation-center" },
] as const;

const headerPanelCss: CSSProperties = {
  background: PANEL_BG,
  borderRadius: 12,
  border: BORDER,
  padding: 12,
  marginBottom: 10,
};

const metricsGridCss: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gridAutoRows: "minmax(0, 1fr)",
  gap: 8,
  flex: 1,
  minHeight: 0,
};

const pageViewportCss: CSSProperties = {
  height: "100vh",
  overflow: "hidden",
};

const dashboardAreaCss: CSSProperties = {
  height: "calc(100vh - 136px)",
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const chartHostCss: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "flex",
};

const cardCss: CSSProperties = {
  background: PANEL_BG,
  border: BORDER,
  borderRadius: 12,
  padding: 12,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

function MisIdDropdown({
  value,
  options,
  onChange,
}: {
  value: string;
  options: OrgOption[];
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.id === value);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 220, width: "100%", maxWidth: 360 }}>
      <button
        type="button"
        onClick={() => setOpen((state) => !state)}
        style={{
          width: "100%",
          height: 36,
          borderRadius: 10,
          border: CONTROL_BORDER,
          background: ACTION_BG,
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 10px",
          cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedOption?.label || "Все доступные"}
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: "auto",
            background: ACTION_BG,
            border: BORDER,
            borderRadius: 10,
            zIndex: 20000,
          }}
        >
          {[{ id: "", label: "Все доступные" }, ...options].map((org) => (
            <button
              key={`mis-${org.id || "all"}`}
              type="button"
              onClick={() => {
                onChange(org.id);
                setOpen(false);
              }}
              style={{
                width: "100%",
                background: org.id === value ? "rgba(255,255,255,0.14)" : "transparent",
                border: "none",
                color: "#fff",
                textAlign: "left",
                padding: "10px 12px",
                cursor: "pointer",
              }}
            >
              {org.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function Center() {
  const [screenId, setScreenId] = useState<ScreenId>(1);
  const [misId, setMisId] = useState("");
  const [misOptions, setMisOptions] = useState<OrgOption[]>([]);
  const [days, setDays] = useState(30);
  const [metrics, setMetrics] = useState<MetricView[]>([]);
  const [queueBars, setQueueBars] = useState<QueueBar[]>([]);
  const [treatmentReasonRows, setTreatmentReasonRows] = useState<TreatmentReasonFlatRow[]>([]);
  const [screen5Data, setScreen5Data] = useState<Screen5Response | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hospitalRefresh, setHospitalRefresh] = useState("");
  const [queueRefresh, setQueueRefresh] = useState("");
  const [treatmentReasonRefresh, setTreatmentReasonRefresh] = useState("");
  const [screen5Refresh, setScreen5Refresh] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const queueDemoMode = useMemo(() => isQueueDemoMode(), []);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1440 : window.innerWidth,
  );

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadBootstrap = async () => {
      try {
        const data = await apiFetch<BootstrapResponse>("/api/v1/situation-center/bootstrap", {});
        if (cancelled) return;
        const ids = Array.isArray(data.available_mis_ids) ? data.available_mis_ids : [];
        const saved = localStorage.getItem("situation_center_mis_id") || "";
        const orgs = await fetchUserOrgsFromDefektolog().catch(() => [] as OrgOption[]);
        const fallbackOptions = ids.map((id) => ({ id, label: id } satisfies OrgOption));
        const nextOptions = ids.length
          ? ids.map((id) => orgs.find((org) => org.id === id) || fallbackOptions.find((option) => option.id === id)!).filter(Boolean)
          : orgs;
        setMisOptions(nextOptions);
        const availableIds = new Set(nextOptions.map((option) => option.id));
        const nextMisId = saved && availableIds.has(saved) ? saved : "";
        setMisId(nextMisId);
        setDays(Number.isFinite(data.default_days) ? Number(data.default_days) : 30);
        setHospitalRefresh(data.last_refresh?.hospital || "");
        setQueueRefresh(data.last_refresh?.queue || "");
      } catch (e) {
        if (!cancelled) setError(String((e as Error)?.message || e));
      }
    };
    void loadBootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("situation_center_mis_id", misId);
  }, [misId]);

  const fetchScreen = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (screenId === 3) {
        const data = await apiFetch<QueueResponse>("/api/v1/situation-center/screen-3", { mis_id: misId || undefined });
        const bars = Array.isArray(data.bars) ? data.bars : [];
        setQueueBars(queueDemoMode ? makeDemoQueueBars(bars) : bars);
        setQueueRefresh(data.last_refresh_at || "");
        setMetrics([]);
        setTreatmentReasonRows([]);
        return;
      }

      if (screenId === 4) {
        const data = await apiFetch<TreatmentReasonResponse>("/api/v1/situation-center/screen-4", {
          mis_id: misId || undefined,
        });
        const groups = Array.isArray(data.groups) ? data.groups : [];
        const flatRows = buildTreatmentReasonRows(groups);
        setTreatmentReasonRows(flatRows);
        setTreatmentReasonRefresh(data.last_refresh_at || "");
        setMetrics([]);
        setQueueBars([]);
        setScreen5Data(null);
        return;
      }

      if (screenId === 5) {
        const data = await apiFetch<Screen5Response>("/api/v1/situation-center/screen-5", {
          mis_id: misId || undefined,
        });
        setScreen5Data(data);
        setScreen5Refresh(data.last_refresh_at || "");
        setMetrics([]);
        setQueueBars([]);
        setTreatmentReasonRows([]);
        return;
      }

      const data = await apiFetch<ScreenResponse>(`/api/v1/situation-center/screen-${screenId}`, {
        mis_id: misId || undefined,
        days,
      });
      const series = Array.isArray(data.series) ? data.series : [];
      const prepared = series.map((s, index) => {
        const points = Array.isArray(s.points) ? s.points : [];
        const values = points.map((point) => point.value);
        return {
          id: s.metric_code,
          title: s.metric_name,
          color: COLORS[index % COLORS.length],
          buckets: points.map((point) => formatPointLabel(point.date)),
          values,
          currentValue: values.length ? values[values.length - 1] ?? 0 : 0,
          periodLabel: getPeriodLabel(points),
        } satisfies MetricView;
      });
      setMetrics(prepared);
      setQueueBars([]);
      setTreatmentReasonRows([]);
      setScreen5Data(null);
      setHospitalRefresh(data.last_refresh_at || "");
    } catch (e) {
      setError(String((e as Error)?.message || e));
    } finally {
      setLoading(false);
    }
  }, [days, misId, queueDemoMode, screenId]);

  useEffect(() => {
    let cancelled = false;

    const runFetch = async () => {
      await fetchScreen();
    };

    void runFetch();
    const timer = window.setInterval(() => {
      if (!cancelled) void runFetch();
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [fetchScreen, reloadTick]);

  const queueLabels = useMemo(() => queueBars.map((item) => item.queue_category_name), [queueBars]);
  const queueTotalValues = useMemo(() => queueBars.map((item) => item.total_value), [queueBars]);
  const queueRemainingValues = useMemo(() => queueBars.map((item) => item.remaining_value), [queueBars]);
  const treatmentReasonChartRows = useMemo(() => treatmentReasonRows, [treatmentReasonRows]);
  const metricsForGrid = useMemo(() => {
    const items = metrics.slice(0, METRIC_CARD_COUNT);
    while (items.length < METRIC_CARD_COUNT) {
      items.push({
        id: `empty-${items.length}`,
        title: "Нет данных",
        color: COLORS[items.length % COLORS.length],
        buckets: [],
        values: [],
        currentValue: 0,
        periodLabel: "Нет данных за выбранный период",
      });
    }
    return items;
  }, [metrics]);

  const isChartScreen = screenId === 3 || screenId === 4 || screenId === 5;
  const pageTitle =
    screenId === 3
      ? "Ситуационный центр — экран 3"
      : screenId === 4
        ? "Ситуационный центр — экран 4"
        : screenId === 5
          ? "Ситуационный центр — экран 5"
        : `Ситуационный центр — экран ${screenId}`;
  const refreshLabel =
    screenId === 3
      ? formatIso(queueRefresh)
      : screenId === 4
        ? formatIso(treatmentReasonRefresh)
        : screenId === 5
          ? formatIso(screen5Refresh)
        : formatIso(hospitalRefresh);
  const isMobile = viewportWidth < 768;
  const treatmentReasonChart = useMemo(
    () => treatmentReasonOption(treatmentReasonChartRows, isMobile),
    [isMobile, treatmentReasonChartRows],
  );
  const responsiveContainerCss: CSSProperties = useMemo(
    () => ({
      ...containerCss,
      padding: isMobile ? "8px" : "10px",
      gap: 0,
    }),
    [isMobile],
  );
  const responsiveDashboardAreaCss: CSSProperties = useMemo(
    () => ({
      ...dashboardAreaCss,
      height: "calc(100vh - 136px)",
      overflow: "hidden",
    }),
    [],
  );
  const responsiveMetricsGridCss: CSSProperties = useMemo(
    () => ({
      ...metricsGridCss,
      gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
      gridTemplateRows: isChartScreen ? undefined : isMobile ? "repeat(4, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
      overflow: "hidden",
    }),
    [isMobile, isChartScreen],
  );
  const metricCardMinHeight = 0;

  return (
    <div style={{ ...pageCss, ...pageViewportCss }}>
      <div
        style={{
          ...responsiveContainerCss,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={headerPanelCss}>
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: isMobile ? "stretch" : "center",
              flexWrap: "wrap",
              justifyContent: "space-between",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}>
              <label style={{ fontSize: 14, color: "#fff", display: "flex", alignItems: isMobile ? "stretch" : "center", gap: 8, flexDirection: isMobile ? "column" : "row" }}>
                подразделение:
                <MisIdDropdown value={misId} options={misOptions} onChange={setMisId} />
              </label>

              <button
                type="button"
                onClick={() => setReloadTick((value) => value + 1)}
                style={{
                  height: 36,
                  padding: "0 12px",
                  borderRadius: 10,
                  border: CONTROL_BORDER,
                  background: ACTION_BG,
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Обновить
              </button>

              <strong style={{ fontSize: isMobile ? 16 : 18 }}>{pageTitle}</strong>
            </div>

            <div style={{ marginLeft: isMobile ? 0 : "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: isMobile ? "space-between" : "flex-end" }}>
              {[1, 2, 3, 4, 5].map((id) => {
                const active = screenId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setScreenId(id as ScreenId)}
                    style={{
                      height: 34,
                      minWidth: 34,
                      padding: "0 10px",
                      borderRadius: 8,
                      border: CONTROL_BORDER,
                      background: active ? "#00B8BA" : ACTION_BG,
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    {id}
                  </button>
                );
              })}

              <details style={{ position: "relative" }}>
                <summary style={{ color: "#fff", cursor: "pointer", listStyle: "none" }}>☰</summary>
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 6px)",
                    background: ACTION_BG,
                    border: BORDER,
                    borderRadius: 8,
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    zIndex: 20000,
                    minWidth: 220,
                  }}
                >
                  {menuRoutes.map((route) => (
                    <a
                      key={route.href}
                      href={route.href}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        background: "transparent",
                        border: "none",
                        textAlign: "left",
                        color: "#fff",
                        cursor: "pointer",
                        padding: "6px 8px",
                        borderRadius: 6,
                        textDecoration: "none",
                      }}
                    >
                      {route.label}
                    </a>
                  ))}
                </div>
              </details>
            </div>
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: TEXT_MUTED }}>
            Последнее обновление: {refreshLabel}
            {screenId === 5 ? " • период: с 1 января" : screenId !== 3 ? ` • период: ${days} дней` : ""}
            {screenId === 3 && queueDemoMode ? " • demo queue mode" : ""}
          </div>
          {loading ? <div style={{ marginTop: 6, fontSize: 12, color: TEXT_MUTED }}>Загрузка...</div> : null}
          {error ? <div style={{ marginTop: 6, color: "#ffb4b4", fontSize: 12, whiteSpace: "pre-wrap" }}>{error}</div> : null}
        </div>

        <div
          style={responsiveDashboardAreaCss}
        >
          {screenId === 3 ? (
            <section style={{ ...cardCss, flex: 1, overflow: "hidden", minHeight: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>Очереди на текущий день</div>
              <ReactECharts
                option={queueOption(queueLabels, queueTotalValues, queueRemainingValues)}
                style={{ width: "100%", height: "100%" }}
                notMerge
                lazyUpdate
              />
            </section>
          ) : screenId === 4 ? (
            <section style={{ ...cardCss, flex: 1, overflowY: "auto", minHeight: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>Поводы обращения на текущий день</div>
              <ReactECharts
                option={treatmentReasonChart.option}
                style={{ width: "100%", height: treatmentReasonChart.chartHeight }}
                notMerge
                lazyUpdate
              />
            </section>
          ) : screenId === 5 && screen5Data ? (
            <section style={{ ...cardCss, flex: 1, overflow: "hidden", minHeight: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>Скрининговые охваты с 1 января</div>
              <ReactECharts
                option={screen5Option(screen5Data)}
                style={{ width: "100%", height: "100%" }}
                notMerge
                lazyUpdate
              />
            </section>
          ) : (
            <main style={responsiveMetricsGridCss}>
              {metricsForGrid.map((metric) => (
                <section key={metric.id} style={{ ...cardCss, overflow: "hidden", minHeight: metricCardMinHeight, height: "100%" }}>
                  <div style={{ fontSize: 14, color: TEXT_MUTED }}>{metric.title}</div>
                  <div style={{ fontSize: 28, lineHeight: "36px", fontWeight: 800, marginTop: 6 }}>{metric.currentValue}</div>
                  <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 4, marginBottom: 8 }}>{metric.periodLabel}</div>
                  <div style={chartHostCss}>
                    <ReactECharts option={lineOption(metric)} style={{ width: "100%", height: "100%" }} notMerge lazyUpdate />
                  </div>
                </section>
              ))}
            </main>
          )}
        </div>
      </div>
    </div>
  );
}
